import { ArrowBigDownDash, ArrowBigDownDashIcon, ArrowBigLeftDashIcon, ChevronDown, ArrowBigRightDash } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({
    solutions: false,
    products: false,
  });

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleDropdown = (menu) => {
    setDropdownOpen({
      ...dropdownOpen,
      [menu]: !dropdownOpen[menu],
    });
  };

  const closeDropdowns = () => {
    setDropdownOpen({
      solutions: false,
      products: false,
    });
  };

  return (
    <header className="fixed left-0 right-0 top-0 z-50 bg-white shadow-sm">
      <div className="relative mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
        {/* Logo */}
        <a href="/" className="text-2xl font-bold text-blue-600 transition-colors hover:text-blue-700">
          DigitConnecting
        </a>

        {/* Navigation */}
        <nav
          className={`flex items-center md:static md:flex md:h-auto md:w-auto md:flex-row md:justify-start md:bg-transparent md:pt-0 md:shadow-none ${isMenuOpen ? 'right-0' : '-right-full'} fixed top-0 h-screen w-3/5 flex-col justify-start bg-white pt-20 shadow-lg transition-all duration-300`}>
          {/* Products Dropdown */}
          <div className="group mx-4 my-4 w-full text-center md:my-0 md:w-auto md:text-left">
            <button className="relative flex w-full items-center justify-center text-lg font-medium text-gray-600 transition-colors hover:text-blue-600 md:justify-start md:text-base">
              {t('header.products')}
              <ChevronDown size={16} className="ml-1 transition-transform duration-200 group-hover:rotate-180" />
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-blue-600 transition-all duration-200 group-hover:w-full"></span>
            </button>

            {/* Dropdown Menu */}
            <div className="left-0 mt-2 block w-full bg-transparent shadow-none md:invisible md:absolute
              md:top-full md:z-10 md:mt-[-1px] md:transform md:rounded-sm md:bg-white md:opacity-0 md:shadow-md
              md:transition-all md:duration-200 md:group-hover:visible md:group-hover:opacity-100">
              <div className='h-[auto] p-4 grid space-x-2 grid-cols-5'>
                {/* Column 1 */}
                <div className='col-span-2'>
                  <h3 className="text-lg font-semibold text-gray-800">PIM & DAM</h3>
                  <p className="text-sm text-gray-500 h-[40px] mt-1 mb-4">
                    Best of class solutions that help you drive revenue
                  </p>

                  <div className='flex'>
                    <ul className="space-y-3 ml-0">
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">PIM Software</p>
                          <p className="text-sm text-gray-500">Product Information Management</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">Workflow</p>
                          <p className="text-sm text-gray-500">Automated Tools</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">PIM for Distributors</p>
                          <p className="text-sm text-gray-500">Aggregating and Enriching Data</p>
                        </div>
                      </li>
                    </ul>
                    <ul className="space-y-3 ml-0">
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">DAM</p>
                          <p className="text-sm text-gray-500">Digital Asset Management</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">PIM for Brands</p>
                          <p className="text-sm text-gray-500">Aggregating and Enriching Data</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">PIM for Retailers</p>
                          <p className="text-sm text-gray-500">How Catsy Helps Retailers Win</p>
                        </div>
                      </li>
                    </ul>
                  </div>

                </div>

                {/* Column 2 */}
                <div className='col-span-1'>
                  <h3 className="text-lg font-semibold text-gray-800">Centralize</h3>
                  <p className="text-sm h-[40px] text-gray-500 mt-1 mb-4">
                    Solve underlying challenges with One Source of truth
                  </p>
                  <ul className="space-y-3 ml-0">
                    <li className="flex items-start gap-3">
                      <ArrowBigRightDash className="text-yellow-500" size={20} />
                      <div className='text-left'>
                        <p className="font-medium text-gray-700">Centralize</p>
                        <p className="text-sm text-gray-500">One hub for your product data</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <ArrowBigRightDash className="text-yellow-500" size={20} />
                      <div>
                        <p className="font-medium text-gray-700">Optimize</p>
                        <p className="text-sm text-gray-500">Make content standout</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <ArrowBigRightDash className="text-yellow-500" size={20} />
                      <div>
                        <p className="font-medium text-gray-700">Publish</p>
                        <p className="text-sm text-gray-500">Export data to channel partners</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <ArrowBigRightDash className="text-yellow-500" size={20} />
                      <div>
                        <p className="font-medium text-gray-700">ERP</p>
                        <p className="text-sm text-gray-500">ERP Integration</p>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Column 3 */}
                <div className='col-span-2'>
                  <h3 className="text-lg font-semibold text-gray-800">Integrations</h3>
                  <p className="text-sm h-[40px] text-gray-500 mt-1 mb-4">
                    Direct Integrations to get your products to market fast
                  </p>

                  <div className="flex">

                    <ul className="space-y-3 ml-0">
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">Shopify</p>
                          <p className="text-sm text-gray-500">Shopify DAM/PIM</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">Magento</p>
                          <p className="text-sm text-gray-500">Magento DAM/PIM</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">Walmart</p>
                          <p className="text-sm text-gray-500">Walmart Marketplace PIM & DAM</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">InDesign</p>
                          <p className="text-sm text-gray-500">InDesign Integration</p>
                        </div>
                      </li>
                    </ul>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">Bigcommerce</p>
                          <p className="text-sm text-gray-500">Bigcommerce DAM/PIM</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">Amazon</p>
                          <p className="text-sm text-gray-500">Amazon DAM/PIM</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowBigRightDash className="text-yellow-500" size={20} />
                        <div>
                          <p className="font-medium text-gray-700">GDSN</p>
                          <p className="text-sm text-green-500">Coming soon</p>
                        </div>
                      </li>
                    </ul>
                  </div>

                </div>


              </div>

              {/* <a
                href="/products/pim"
                onClick={closeMenu}
                className="block px-4 py-2 text-center text-gray-600 transition-colors hover:bg-gray-50 hover:text-blue-600 md:text-left">
                {t('products.pim.title')}
              </a>
              <a
                href="/products/dam"
                onClick={closeMenu}
                className="block px-4 py-2 text-center text-gray-600 transition-colors hover:bg-gray-50 hover:text-blue-600 md:text-left">
                {t('products.dam.title')}
              </a>
              <a
                href="/products/mdm"
                onClick={closeMenu}
                className="block px-4 py-2 text-center text-gray-600 transition-colors hover:bg-gray-50 hover:text-blue-600 md:text-left">
                {t('products.mdm.title')}
              </a>
              <a
                href="/products/cdp"
                onClick={closeMenu}
                className="block px-4 py-2 text-center text-gray-600 transition-colors hover:bg-gray-50 hover:text-blue-600 md:text-left">
                {t('products.cdp.title')}
              </a> */}
            </div>
          </div>

          {/* Regular Nav Items */}
          <div className="mx-4 my-4 w-full text-center md:my-0 md:w-auto md:text-left">
            <a
              href="/pricing"
              onClick={closeMenu}
              className="group relative text-lg font-medium text-gray-600 transition-colors hover:text-blue-600 md:text-base">
              {t('header.pricing')}
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-blue-600 transition-all duration-200 group-hover:w-full"></span>
            </a>
          </div>

          <div className="mx-4 my-4 w-full text-center md:my-0 md:w-auto md:text-left">
            <a
              href="/partners"
              onClick={closeMenu}
              className="group relative text-lg font-medium text-gray-600 transition-colors hover:text-blue-600 md:text-base">
              {t('header.partners')}
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-blue-600 transition-all duration-200 group-hover:w-full"></span>
            </a>
          </div>

          <div className="mx-4 my-4 w-full text-center md:my-0 md:w-auto md:text-left">
            <a
              href="/resources"
              onClick={closeMenu}
              className="group relative text-lg font-medium text-gray-600 transition-colors hover:text-blue-600 md:text-base">
              {t('header.resources')}
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-blue-600 transition-all duration-200 group-hover:w-full"></span>
            </a>
          </div>

          {/* Language Selector */}
          <div className="my-4 ml-4 flex items-center justify-center md:my-0 md:justify-start">
            <button
              onClick={() => changeLanguage('en')}
              className={`mx-1 cursor-pointer px-2 py-1 transition-colors ${i18n.language === 'en' ? 'font-bold text-blue-600' : 'font-normal text-gray-600 hover:text-gray-800'
                }`}>
              EN
            </button>
            <button
              onClick={() => changeLanguage('zh')}
              className={`mx-1 cursor-pointer px-2 py-1 transition-colors ${i18n.language === 'zh' ? 'font-bold text-blue-600' : 'font-normal text-gray-600 hover:text-gray-800'
                }`}>
              中文
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
